import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { findPersonalProfile } from '../partials/adminFormat';

// A 401 is rethrown as is: authMiddleware logs out on its error message, which rejectWithValue would replace.
// step tells which write of a flow failed: 'permission' (the personal profile) or 'user'.
// error.data: a refusal of the front itself, before any request.
const adminThunk = (name, flow) => createAsyncThunk(`admin/${name}`, async (arg, thunkAPI) => {
  try {
    return await flow(arg, thunkAPI);
  } catch (error) {
    if (error.response?.status === 401) throw error;
    return thunkAPI.rejectWithValue({ status: error.response?.status ?? null, data: error.response?.data ?? error.data ?? null, step: error.step ?? null });
  }
});

const userPath = (login) => `/users/${encodeURIComponent(login)}`;

const step = async (name, request) => {
  try {
    return (await request).data;
  } catch (error) {
    throw Object.assign(error, { step: name });
  }
};

// Best effort: the user write that needed it failed or the user is gone, the profile is only left over.
const dropProfile = (id) => axiosInstance.delete(`/permissions/${id}`).then(() => true, () => false);

export const fetchUsers = adminThunk('fetchUsers', async () => (await axiosInstance.get('/users')).data);
export const fetchPermissions = adminThunk('fetchPermissions', async () => (await axiosInstance.get('/permissions')).data);
export const regenerateApiKey = adminThunk('regenerateApiKey', async (login) => (await axiosInstance.post(`${userPath(login)}/apiKey`)).data);

export const createPermission = adminThunk('createPermission', async (profile) => (await axiosInstance.post('/permissions', profile)).data);
export const updatePermission = adminThunk('updatePermission', async ({ _id, ...profile }) => (await axiosInstance.put(`/permissions/${_id}`, profile)).data);
export const deletePermission = adminThunk('deletePermission', async (id) => {
  await axiosInstance.delete(`/permissions/${id}`);
  return null;
});

// A user carries shared profiles (sharedIds) and, for its own rights, a personal profile named after its login.
// own: { permissions, admin } of the personal profile, or null for none. user === null: a new user.
// A personal profile left over (nobody carries it) is taken back; one that another user carries is never changed.
export const saveUser = adminThunk('saveUser', async ({ user, login, sharedIds, own, rateLimit }, { getState, dispatch }) => {
  const state = getState();
  const { profile: named, shared } = findPersonalProfile(user, login, state.admin.users, state.admin.permissions);
  const writeUser = (ids) => {
    const body = { permissions: ids, rateLimit };
    return step('user', (user === null) ? axiosInstance.post('/users', { login, ...body }) : axiosInstance.put(userPath(login), body));
  };
  if (own === null) {
    const saved = await writeUser(sharedIds);
    // Its own rights are taken back: the personal profile it carried is left over.
    const carried = (named !== null) && !shared && (user?.permissions.some(({ _id }) => _id === named._id) ?? false);
    const dropped = carried && await dropProfile(named._id);
    return { user: saved, permission: null, droppedId: dropped ? named._id : null };
  }
  if (shared) throw Object.assign(new Error('Shared profile'), { step: 'permission', data: { reason: 'shared', name: login } });
  const profile = { name: login, ...own };
  // A write done before a refused one: the lists are read again so they show what the back holds.
  const reloadAfter = (error) => {
    dispatch(fetchUsers());
    if (state.user.permissions.admin.permissions.read) dispatch(fetchPermissions());
    throw error;
  };
  if ((named !== null) && (user !== null)) {
    // The user first: when the back refuses it (403 rights, 409 self), nothing is written.
    const saved = await writeUser([ ...sharedIds, named._id ]);
    try {
      return { user: saved, permission: await step('permission', axiosInstance.put(`/permissions/${named._id}`, profile)), droppedId: null };
    } catch (error) {
      return reloadAfter(error);
    }
  }
  if (named !== null) {
    const permission = await step('permission', axiosInstance.put(`/permissions/${named._id}`, profile));
    try {
      return { user: await writeUser([ ...sharedIds, permission._id ]), permission, droppedId: null };
    } catch (error) {
      return reloadAfter(error);
    }
  }
  const permission = await step('permission', axiosInstance.post('/permissions', profile));
  try {
    return { user: await writeUser([ ...sharedIds, permission._id ]), permission, droppedId: null };
  } catch (error) {
    await dropProfile(permission._id);
    throw error;
  }
});

// Its personal profile goes with the user; the back refuses (409 assigned) to delete one another user still carries.
export const deleteUser = adminThunk('deleteUser', async (user) => {
  await step('user', axiosInstance.delete(userPath(user.login)));
  const personal = user.permissions.find((profile) => profile.name === user.login) ?? null;
  const dropped = (personal !== null) && await dropProfile(personal._id);
  return { login: user.login, permissionId: dropped ? personal._id : null };
});

const noResponse = { thunk: null, status: null, error: null };
const thunkName = (action) => action.type.split('/')[1];
const replaceById = (list, item) => list.map((current) => (current._id === item._id) ? item : current);
const upsertById = (list, item) => list.some((current) => current._id === item._id) ? replaceById(list, item) : [ ...list, item ];

export const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    users: [],
    permissions: [],
    apiResponse: noResponse,
  },
  reducers: {
    resetAdminApiResponse: (state) => {
      state.apiResponse = noResponse;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload;
      })
      .addCase(saveUser.fulfilled, (state, action) => {
        const { user, permission, droppedId } = action.payload;
        // The user first: it may come back with the profile as it was before the profile write.
        if (user) state.users = upsertById(state.users, user);
        if (permission) {
          state.permissions = upsertById(state.permissions, permission);
          // The users list carries its profiles populated.
          state.users = state.users.map((current) => ({ ...current, permissions: replaceById(current.permissions, permission) }));
        }
        if (droppedId) state.permissions = state.permissions.filter((current) => current._id !== droppedId);
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        state.permissions.push(action.payload);
      })
      .addCase(updatePermission.fulfilled, (state, action) => {
        state.permissions = replaceById(state.permissions, action.payload);
        state.users = state.users.map((current) => ({ ...current, permissions: replaceById(current.permissions, action.payload) }));
      })
      .addCase(deletePermission.fulfilled, (state, action) => {
        state.permissions = state.permissions.filter((current) => current._id !== action.meta.arg);
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.login !== action.payload.login);
        state.permissions = state.permissions.filter((permission) => permission._id !== action.payload.permissionId);
      })
      .addMatcher((action) => action.type.startsWith('admin/') && action.type.endsWith('/fulfilled') && !thunkName(action).startsWith('fetch'), (state, action) => {
        state.apiResponse = { thunk: thunkName(action), status: 'success', error: null };
      })
      .addMatcher((action) => action.type.startsWith('admin/') && action.type.endsWith('/rejected'), (state, action) => {
        state.apiResponse = { thunk: thunkName(action), status: 'error', error: action.payload ?? null };
      });
  },
});

export const { resetAdminApiResponse } = adminSlice.actions;

export default adminSlice.reducer;
